import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Tabs,
  Tab,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  PaymentsOutlined,
  PaidOutlined,
  AddCardOutlined,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import StatCard from '../components/StatCard';
import EmptyState from '../components/EmptyState';
import LoadingState from '../components/LoadingState';
import { financeService, couriersService, restaurantsService } from '../api/services';
import { useLanguage } from '../context/LanguageContext';
import { getApiErrorMessage } from '../utils/errorHandler';

const Finance = () => {
  const { t, isRtl, dir } = useLanguage();
  const [tabIndex, setTabIndex] = useState(0);
  const [advances, setAdvances] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [couriers, setCouriers] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submittingAdvance, setSubmittingAdvance] = useState(false);
  const [submittingSettle, setSubmittingSettle] = useState(false);

  // Advance Modal
  const [advanceModalOpen, setAdvanceModalOpen] = useState(false);
  const [newAdvance, setNewAdvance] = useState({
    courierId: '',
    advanceDate: new Date().toISOString().split('T')[0],
    amount: '',
    reason: '',
  });

  // Settlement Modal
  const [settleModalOpen, setSettleModalOpen] = useState(false);
  const [newSettlement, setNewSettlement] = useState({
    partyType: 3, // 3 = Courier, 2 = Restaurant
    partyId: '',
    fromDate: new Date().toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0],
    settlementAmount: '',
  });

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const fetchFinanceData = async () => {
    setLoading(true);
    try {
      const [advRes, setRes, couriersRes, restRes] = await Promise.allSettled([
        financeService.getAdvances(),
        financeService.getSettlements(),
        couriersService.getCouriers(),
        restaurantsService.getRestaurants(),
      ]);

      if (advRes.status === 'fulfilled' && advRes.value) {
        const rawAdv = Array.isArray(advRes.value) ? advRes.value : (advRes.value?.items || advRes.value?.data || []);
        const normAdv = rawAdv.map((adv) => ({
          ...adv,
          id: adv.courierAdvanceId || adv.id,
          courierAdvanceId: adv.courierAdvanceId || adv.id,
          advanceNumber: adv.advanceNumber || `ADV-${adv.courierAdvanceId || adv.id}`,
          courierId: adv.courierId,
          courierCode: adv.courierCode || '',
          courierName: adv.courierName || (isRtl ? 'كابتن' : 'Courier'),
          advanceDate: adv.advanceDate || (adv.createdAtUtc ? adv.createdAtUtc.split('T')[0] : new Date().toISOString().split('T')[0]),
          amount: parseFloat(adv.amount) || 0,
          recoveredAmount: parseFloat(adv.recoveredAmount) || 0,
          outstandingAmount: parseFloat(adv.outstandingAmount) || 0,
          reason: adv.reason || adv.notes || '-',
          status: adv.status,
          journalEntryId: adv.journalEntryId,
        }));
        setAdvances(normAdv);
      } else {
        setAdvances([]);
      }

      if (setRes.status === 'fulfilled' && setRes.value) {
        const rawSet = Array.isArray(setRes.value) ? setRes.value : (setRes.value?.items || setRes.value?.data || []);
        const normSet = rawSet.map((s) => ({
          ...s,
          id: s.settlementId || s.id,
          settlementId: s.settlementId || s.id,
          settlementNumber: s.settlementNumber || `SET-${s.settlementId || s.id}`,
          partyType: s.partyType,
          partyTypeName: s.partyType === 2 ? (isRtl ? 'مطعم' : 'Restaurant') : (isRtl ? 'كابتن' : 'Courier'),
          partyId: s.partyId,
          partyCode: s.partyCode || '',
          partyName: s.partyName || (s.partyType === 2 ? (isRtl ? 'مطعم' : 'Restaurant') : (isRtl ? 'كابتن' : 'Courier')),
          fromDate: s.fromDate || (s.createdAtUtc ? s.createdAtUtc.split('T')[0] : ''),
          toDate: s.toDate || s.fromDate || (s.createdAtUtc ? s.createdAtUtc.split('T')[0] : ''),
          openingBalance: parseFloat(s.openingBalance) || 0,
          debitTotal: parseFloat(s.debitTotal) || 0,
          creditTotal: parseFloat(s.creditTotal) || 0,
          closingBalance: parseFloat(s.closingBalance) || 0,
          settlementAmount: parseFloat(s.settlementAmount) || 0,
          status: s.status,
          createdAtUtc: s.createdAtUtc,
          approvedAtUtc: s.approvedAtUtc,
        }));
        setSettlements(normSet);
      } else {
        setSettlements([]);
      }

      if (couriersRes.status === 'fulfilled' && couriersRes.value) {
        const raw = Array.isArray(couriersRes.value) ? couriersRes.value : (couriersRes.value?.items || couriersRes.value?.data || []);
        const norm = raw.map((c) => {
          const cId = c.courierId ?? c.id ?? c.CourierId ?? c.Id;
          const cName = c.fullNameAr || c.fullNameEn || c.fullName || c.name || `كابتن #${cId}`;
          const cPhone = c.mobile || c.phone || '';
          return {
            ...c,
            id: cId,
            courierId: cId,
            name: cName,
            fullName: cName,
            fullNameAr: c.fullNameAr || cName,
            phone: cPhone,
            mobile: cPhone,
          };
        });
        setCouriers(norm);
      }

      if (restRes.status === 'fulfilled' && restRes.value) {
        const rawRest = Array.isArray(restRes.value) ? restRes.value : (restRes.value?.items || restRes.value?.data || []);
        const normRest = rawRest.map((r) => ({
          ...r,
          id: r.id || r.restaurantId || r.Id,
          name: r.restaurantNameAr || r.name || r.restaurantName || `مطعم #${r.id || r.restaurantId}`,
        }));
        setRestaurants(normRest);
      }
    } catch (e) {
      console.warn('Finance fetch error:', e);
      setAdvances([]);
      setSettlements([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const handleIssueAdvance = async (e) => {
    if (e) e.preventDefault();
    if (!newAdvance.courierId || !newAdvance.amount) {
      setToast({ open: true, message: isRtl ? 'يرجى اختيار الكابتن وإدخال مبلغ السلفة' : 'Please select courier and enter amount', severity: 'error' });
      return;
    }

    setSubmittingAdvance(true);
    try {
      await financeService.createAdvance({
        courierId: Number(newAdvance.courierId),
        advanceDate: newAdvance.advanceDate || new Date().toISOString().split('T')[0],
        amount: parseFloat(newAdvance.amount),
        reason: newAdvance.reason?.trim() || '',
      });
      setToast({ open: true, message: isRtl ? 'تم صرف السلفة وتوثيقها بنجاح!' : 'Advance issued successfully!', severity: 'success' });
      setAdvanceModalOpen(false);
      setNewAdvance({
        courierId: '',
        advanceDate: new Date().toISOString().split('T')[0],
        amount: '',
        reason: '',
      });
      fetchFinanceData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل صرف السلفة' : 'Failed to issue advance'), severity: 'error' });
    } finally {
      setSubmittingAdvance(false);
    }
  };

  const handleRunSettlement = async (e) => {
    if (e) e.preventDefault();
    if (!newSettlement.partyId) {
      setToast({ open: true, message: isRtl ? 'يرجى اختيار الطرف المراد تسويته' : 'Please select party to settle', severity: 'error' });
      return;
    }

    setSubmittingSettle(true);
    try {
      await financeService.createSettlement({
        partyType: Number(newSettlement.partyType),
        partyId: Number(newSettlement.partyId),
        fromDate: newSettlement.fromDate,
        toDate: newSettlement.toDate,
        settlementAmount: parseFloat(newSettlement.settlementAmount) || 0,
      });
      setToast({ open: true, message: isRtl ? 'تم إجراء وإغلاق التسوية بنجاح!' : 'Settlement closed successfully!', severity: 'success' });
      setSettleModalOpen(false);
      setNewSettlement({
        partyType: 3,
        partyId: '',
        fromDate: new Date().toISOString().split('T')[0],
        toDate: new Date().toISOString().split('T')[0],
        settlementAmount: '',
      });
      fetchFinanceData();
    } catch (err) {
      setToast({ open: true, message: getApiErrorMessage(err, isRtl ? 'فشل إجراء التسوية' : 'Failed to run settlement'), severity: 'error' });
    } finally {
      setSubmittingSettle(false);
    }
  };

  return (
    <Layout>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ textAlign: isRtl ? 'right' : 'left' }}>
          <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.2 }}>
            {t('financePage.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('financePage.subtitle')}
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddCardOutlined sx={{ fontSize: 16 }} />}
            onClick={() => setAdvanceModalOpen(true)}
            sx={{ borderRadius: '8px', fontSize: '0.78rem', py: 0.6 }}
          >
            {t('financePage.recordAdvance')}
          </Button>

          <Button
            variant="contained"
            size="small"
            startIcon={<PaidOutlined sx={{ fontSize: 16 }} />}
            onClick={() => setSettleModalOpen(true)}
            sx={{
              borderRadius: '8px',
              fontSize: '0.78rem',
              py: 0.6,
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
            }}
          >
            {t('financePage.settleAccount')}
          </Button>
        </Box>
      </Box>

      {/* Finance Stats Grid */}
      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'إجمالي السلف المنصرفة' : 'Total Advances Issued'}
            value={`${advances.reduce((acc, a) => acc + (a.amount || 0), 0).toFixed(2)} ${t('currency')}`}
            icon={PaidOutlined}
            color="#EF6C00"
            subtitle={`${advances.length} ${isRtl ? 'سلفة مسجلة' : 'advances'}`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'رصيد السلف القائمة (المتبقي)' : 'Outstanding Advances'}
            value={`${advances.reduce((acc, a) => acc + (a.outstandingAmount || 0), 0).toFixed(2)} ${t('currency')}`}
            icon={PaymentsOutlined}
            color="#EF4444"
            subtitle={isRtl ? 'مستحق استرداده من الكباتن' : 'To be recovered from couriers'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'السلف المستردة' : 'Recovered Advances'}
            value={`${advances.reduce((acc, a) => acc + (a.recoveredAmount || 0), 0).toFixed(2)} ${t('currency')}`}
            icon={PaymentsOutlined}
            color="#10B981"
            subtitle={isRtl ? 'تم استقطاعها بنجاح' : 'Successfully collected'}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title={isRtl ? 'إجمالي التسويات' : 'Total Settlements'}
            value={`${settlements.reduce((acc, s) => acc + (parseFloat(s.totalCashCollected || s.settlementAmount) || 0), 0).toFixed(2)} ${t('currency')}`}
            icon={PaymentsOutlined}
            color="#3B82F6"
            subtitle={`${settlements.length} ${isRtl ? 'تسوية مقفلة' : 'settlements closed'}`}
          />
        </Grid>
      </Grid>

      {/* Tabs */}
      <Card sx={{ mb: 2, direction: dir }} dir={dir}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, direction: dir, display: 'flex', justifyContent: 'flex-start' }} dir={dir}>
          <Tabs
            value={tabIndex}
            onChange={(e, val) => setTabIndex(val)}
            textColor="primary"
            indicatorColor="primary"
            dir={dir}
            sx={{
              direction: dir,
              width: '100%',
              '& .MuiTabs-scroller': {
                direction: dir,
              },
              '& .MuiTabs-flexContainer': {
                direction: dir,
                flexDirection: 'row',
                justifyContent: 'flex-start',
              },
            }}
          >
            <Tab label={`${t('financePage.tabs.settlements')} (${settlements.length})`} sx={{ fontWeight: 700, fontSize: '0.82rem' }} />
            <Tab label={`${t('financePage.tabs.advances')} (${advances.length})`} sx={{ fontWeight: 700, fontSize: '0.82rem' }} />
          </Tabs>
        </Box>

        {/* Tab 0: Settlements */}
        {tabIndex === 0 && (
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <LoadingState />
            ) : settlements.length === 0 ? (
              <EmptyState message={t('noData')} subtitle={isRtl ? 'لم يتم تسجيل تسويات مالية بعد' : 'No settlements recorded yet'} />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 900 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'رقم التسوية' : 'Settlement #'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 180 }}>{isRtl ? 'الطرف المعني' : 'Party'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'الفترة' : 'Period'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{isRtl ? 'الافتتاحي' : 'Opening'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'مدين / دائن' : 'Debit/Credit'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'مبلغ التسوية' : 'Settled Amount'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{isRtl ? 'الختامي' : 'Closing'}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 110 }}>{t('status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settlements.map((s) => {
                      const isApproved = s.status === 2 || s.status === 'Approved';
                      const isDraft = s.status === 1 || s.status === 'Draft';
                      return (
                        <TableRow key={s.id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.78rem' }}>
                              {s.settlementNumber?.slice(0, 16) || `#${s.id}`}...
                            </Typography>
                            {s.approvedAtUtc && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                {s.approvedAtUtc.split('T')[0]}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Chip
                                label={s.partyTypeName}
                                size="small"
                                variant="outlined"
                                color={s.partyType === 2 ? 'primary' : 'secondary'}
                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700 }}
                              />
                              <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                {s.partyName}
                              </Typography>
                            </Box>
                            {s.partyCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                {isRtl ? 'كود:' : 'Code:'} {s.partyCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                            {s.fromDate === s.toDate ? s.fromDate : `${s.fromDate} ➔ ${s.toDate}`}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap', fontSize: '0.8rem' }}>
                            {s.openingBalance.toFixed(2)} {t('currency')}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600, display: 'block' }}>
                              +{s.debitTotal.toFixed(2)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 600, display: 'block' }}>
                              -{s.creditTotal.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.84rem' }}>
                              {s.settlementAmount.toFixed(2)} {t('currency')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap', fontWeight: 700, fontSize: '0.8rem' }}>
                            {s.closingBalance.toFixed(2)} {t('currency')}
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={isApproved ? (isRtl ? 'معتمدة ومرحلة' : 'Approved') : (isDraft ? (isRtl ? 'مسودة' : 'Draft') : (isRtl ? 'ملغاة' : 'Cancelled'))}
                              color={isApproved ? 'success' : (isDraft ? 'warning' : 'default')}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}

        {/* Tab 1: Advances */}
        {tabIndex === 1 && (
          <CardContent sx={{ p: 0 }}>
            {loading ? (
              <LoadingState />
            ) : advances.length === 0 ? (
              <EmptyState message={t('noData')} subtitle={isRtl ? 'يمكنك صرف سلفة نقدية جديدة لكابتن من الزر أعلاه' : 'No advances recorded yet'} />
            ) : (
              <TableContainer>
                <Table size="small" sx={{ minWidth: 850 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'رقم السلفة' : 'Advance #'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 170 }}>{t('couriersPage.tableHeaders.courier')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{t('date')}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 120 }}>{isRtl ? 'مبلغ السلفة' : 'Amount'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{isRtl ? 'المسترد' : 'Recovered'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 110 }}>{isRtl ? 'المتبقي' : 'Outstanding'}</TableCell>
                      <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', width: 140 }}>{isRtl ? 'السبب' : 'Reason'}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 110 }}>{t('status')}</TableCell>
                      <TableCell sx={{ textAlign: 'center !important', width: 110 }}>{t('actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {advances.map((adv) => {
                      const isPending = adv.status === 1 || adv.status === 'Active' || adv.outstandingAmount > 0;
                      const isDone = adv.status === 2 || adv.status === 'FullyRecovered' || (adv.outstandingAmount === 0 && adv.amount > 0);
                      return (
                        <TableRow key={adv.id} hover>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.78rem' }}>
                              {adv.advanceNumber?.slice(0, 16) || `#${adv.id}`}...
                            </Typography>
                            {adv.journalEntryId && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', display: 'block' }}>
                                {isRtl ? 'قيد يومية:' : 'JE:'} #{adv.journalEntryId}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                              {adv.courierName}
                            </Typography>
                            {adv.courierCode && (
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem' }}>
                                {isRtl ? 'كود:' : 'Code:'} {adv.courierCode}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', fontSize: '0.78rem' }}>
                            {adv.advanceDate}
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                              {adv.amount.toFixed(2)} {t('currency')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main', fontSize: '0.82rem' }}>
                              {adv.recoveredAmount.toFixed(2)} {t('currency')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important', whiteSpace: 'nowrap' }}>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: adv.outstandingAmount > 0 ? 'error.main' : 'text.secondary', fontSize: '0.82rem' }}>
                              {adv.outstandingAmount.toFixed(2)} {t('currency')}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ textAlign: isRtl ? 'right !important' : 'left !important' }}>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {adv.reason}
                            </Typography>
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            <Chip
                              label={isDone ? (isRtl ? 'مسددة بالكامل' : 'Fully Recovered') : (isRtl ? 'سارية / غير مسددة' : 'Outstanding')}
                              color={isDone ? 'success' : 'warning'}
                              size="small"
                              sx={{ fontWeight: 700, fontSize: '0.7rem' }}
                            />
                          </TableCell>
                          <TableCell align="center" sx={{ textAlign: 'center !important' }}>
                            {isPending ? (
                              <Button
                                size="small"
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                  setNewSettlement({
                                    partyType: 3,
                                    partyId: adv.courierId,
                                    fromDate: adv.advanceDate,
                                    toDate: new Date().toISOString().split('T')[0],
                                    settlementAmount: adv.outstandingAmount,
                                  });
                                  setSettleModalOpen(true);
                                }}
                                sx={{ fontSize: '0.72rem', py: 0.2, px: 1, borderRadius: '6px', whiteSpace: 'nowrap' }}
                              >
                                {isRtl ? 'تسديد / تسوية' : 'Settle'}
                              </Button>
                            ) : (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        )}
      </Card>

      {/* Advance Modal */}
      <Dialog
        open={advanceModalOpen}
        onClose={() => setAdvanceModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'صرف سلفة نقدية لمندوب' : 'Issue Courier Advance'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleIssueAdvance} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField
              select
              size="small"
              label={isRtl ? 'اختر الكابتن' : 'Select Courier'}
              fullWidth
              value={newAdvance.courierId}
              onChange={(e) => setNewAdvance({ ...newAdvance, courierId: e.target.value })}
              required
            >
              {couriers.length === 0 ? (
                <MenuItem disabled value="">
                  {isRtl ? 'لا يوجد كباتن متاحين حالياً' : 'No couriers available'}
                </MenuItem>
              ) : (
                couriers.map((c) => {
                  const cId = c.id ?? c.courierId;
                  const cName = c.fullNameAr || c.name || c.fullName || `كابتن #${cId}`;
                  const cPhone = c.mobile || c.phone;
                  return (
                    <MenuItem key={cId} value={cId}>
                      {cName} {cPhone ? `(${cPhone})` : ''}
                    </MenuItem>
                  );
                })
              )}
            </TextField>

            <TextField
              type="date"
              size="small"
              label={isRtl ? 'تاريخ السلفة' : 'Advance Date'}
              fullWidth
              value={newAdvance.advanceDate}
              onChange={(e) => setNewAdvance({ ...newAdvance, advanceDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              type="number"
              size="small"
              label={isRtl ? 'مبلغ السلفة (بالجنيه) *' : 'Advance Amount (EGP) *'}
              fullWidth
              value={newAdvance.amount}
              onChange={(e) => setNewAdvance({ ...newAdvance, amount: e.target.value })}
              placeholder="500"
              required
            />

            <TextField
              label={isRtl ? 'سبب السلفة والملاحظات' : 'Reason & Notes'}
              fullWidth
              size="small"
              multiline
              rows={2}
              value={newAdvance.reason}
              onChange={(e) => setNewAdvance({ ...newAdvance, reason: e.target.value })}
              placeholder={isRtl ? 'أدخل سبب السلفة...' : 'Enter reason...'}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleIssueAdvance}
            disabled={submittingAdvance}
            startIcon={submittingAdvance ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            sx={{
              background: 'linear-gradient(135deg, #EF6C00 0%, #E65100 100%)',
              fontWeight: 700,
            }}
          >
            {submittingAdvance ? (isRtl ? 'جاري الحفظ...' : 'Saving...') : t('save')}
          </Button>
          <Button onClick={() => setAdvanceModalOpen(false)} color="inherit" size="small" disabled={submittingAdvance}>
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Settle Modal */}
      <Dialog
        open={settleModalOpen}
        onClose={() => setSettleModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px', direction: dir, p: 1 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: '0.95rem', pb: 1, textAlign: isRtl ? 'right' : 'left' }}>
          {isRtl ? 'إجراء تسوية مالية' : 'Create Financial Settlement'}
        </DialogTitle>
        <DialogContent>
          <Box component="form" noValidate onSubmit={handleRunSettlement} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <TextField
              select
              size="small"
              label={isRtl ? 'نوع الطرف' : 'Party Type'}
              fullWidth
              value={newSettlement.partyType}
              onChange={(e) => setNewSettlement({ ...newSettlement, partyType: Number(e.target.value), partyId: '' })}
            >
              <MenuItem value={3}>{isRtl ? 'كابتن توصيل (Courier)' : 'Courier'}</MenuItem>
              <MenuItem value={2}>{isRtl ? 'مطعم / شريك (Restaurant)' : 'Restaurant'}</MenuItem>
            </TextField>

            <TextField
              select
              size="small"
              label={newSettlement.partyType === 3 ? (isRtl ? 'اختر الكابتن' : 'Select Courier') : (isRtl ? 'اختر المطعم' : 'Select Restaurant')}
              fullWidth
              value={newSettlement.partyId}
              onChange={(e) => setNewSettlement({ ...newSettlement, partyId: e.target.value })}
              required
            >
              {newSettlement.partyType === 3 ? (
                couriers.length === 0 ? (
                  <MenuItem disabled value="">{isRtl ? 'لا يوجد كباتن متاحين' : 'No couriers available'}</MenuItem>
                ) : (
                  couriers.map((c) => {
                    const cId = c.id ?? c.courierId;
                    const cName = c.fullNameAr || c.name || c.fullName || `كابتن #${cId}`;
                    const cPhone = c.mobile || c.phone;
                    return (
                      <MenuItem key={cId} value={cId}>
                        {cName} {cPhone ? `(${cPhone})` : ''}
                      </MenuItem>
                    );
                  })
                )
              ) : (
                restaurants.length === 0 ? (
                  <MenuItem disabled value="">{isRtl ? 'لا توجد مطاعم مسجلة' : 'No restaurants available'}</MenuItem>
                ) : (
                  restaurants.map((r) => {
                    const rId = r.id || r.restaurantId;
                    const rName = r.restaurantNameAr || r.name || `مطعم #${rId}`;
                    return (
                      <MenuItem key={rId} value={rId}>
                        {rName}
                      </MenuItem>
                    );
                  })
                )
              )}
            </TextField>

            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  size="small"
                  label={isRtl ? 'من تاريخ' : 'From Date'}
                  fullWidth
                  value={newSettlement.fromDate}
                  onChange={(e) => setNewSettlement({ ...newSettlement, fromDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  type="date"
                  size="small"
                  label={isRtl ? 'إلى تاريخ' : 'To Date'}
                  fullWidth
                  value={newSettlement.toDate}
                  onChange={(e) => setNewSettlement({ ...newSettlement, toDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>

            <TextField
              type="number"
              size="small"
              label={isRtl ? 'مبلغ التسوية (ج.م)' : 'Settlement Amount (EGP)'}
              fullWidth
              value={newSettlement.settlementAmount}
              onChange={(e) => setNewSettlement({ ...newSettlement, settlementAmount: e.target.value })}
              placeholder="0.00"
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: isRtl ? 'flex-start' : 'flex-end', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={handleRunSettlement}
            disabled={submittingSettle}
            startIcon={submittingSettle ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
            color="secondary"
            sx={{ fontWeight: 700 }}
          >
            {submittingSettle ? (isRtl ? 'جاري الترحيل...' : 'Processing...') : (isRtl ? 'ترحيل وإغلاق التسوية' : 'Confirm Settlement')}
          </Button>
          <Button onClick={() => setSettleModalOpen(false)} color="inherit" size="small" disabled={submittingSettle}>
            {t('cancel')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: isRtl ? 'left' : 'right' }}
      >
        <Alert severity={toast.severity} sx={{ borderRadius: '8px', fontWeight: 600 }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
};

export default Finance;
